// src/components/TierButton.tsx
import React2 from "react";
import { TouchableOpacity, Text, View, ActivityIndicator } from "react-native";
function TierButton({
  variant = "primary",
  size = "md",
  onPress,
  disabled = false,
  loading = false,
  children,
  icon,
  iconPosition = "left",
  fullWidth = false,
  className = "",
  testID
}) {
  const baseClasses = "items-center justify-center transition-tier flex-row";
  const radiusClass = "rounded-tier";
  const sizeClasses = {
    sm: "px-4 py-2",
    md: "px-6 py-3",
    lg: "px-8 py-4"
  };
  const textSizeClasses = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg"
  };
  const variantClasses = {
    primary: "bg-primary",
    secondary: "border-2 border-primary bg-transparent",
    ghost: "bg-transparent"
  };
  const textVariantClasses = {
    primary: "text-white font-semibold",
    secondary: "text-primary font-semibold",
    ghost: "text-gray-700 font-medium"
  };
  const disabledClasses = disabled || loading ? "opacity-50" : "active:opacity-80";
  const widthClass = fullWidth ? "w-full" : "";
  const spinnerColor = variant === "primary" ? "white" : "#4A90E2";
  const iconGap = icon ? "gap-2" : "";
  return /* @__PURE__ */ React2.createElement(
    TouchableOpacity,
    {
      onPress,
      disabled: disabled || loading,
      testID,
      className: `${baseClasses} ${radiusClass} ${sizeClasses[size]} ${variantClasses[variant]} ${disabledClasses} ${widthClass} ${iconGap} ${className}`,
      accessibilityRole: "button",
      accessibilityState: {
        disabled: disabled || loading,
        busy: loading
      },
      accessibilityLabel: typeof children === "string" ? children : void 0
    },
    loading ? /* @__PURE__ */ React2.createElement(ActivityIndicator, { size: size === "sm" ? "small" : "small", color: spinnerColor }) : /* @__PURE__ */ React2.createElement(React2.Fragment, null, icon && iconPosition === "left" && /* @__PURE__ */ React2.createElement(View, null, icon), /* @__PURE__ */ React2.createElement(Text, { className: `${textVariantClasses[variant]} ${textSizeClasses[size]}` }, children), icon && iconPosition === "right" && /* @__PURE__ */ React2.createElement(View, null, icon))
  );
}

// src/components/TierCard.tsx
import React3 from "react";
import { View as View2 } from "react-native";
function TierCard({
  variant = "default",
  children,
  className = "",
  testID
}) {
  const baseClasses = "p-6 shadow-md transition-tier";
  const radiusClass = "rounded-tier";
  const variantClasses = {
    default: "bg-white border border-gray-200",
    premium: "bg-accent-blush border-l-4 border-accent-gold",
    editorial: "bg-black border border-accent-gold"
  };
  return /* @__PURE__ */ React3.createElement(View2, { className: `${baseClasses} ${radiusClass} ${variantClasses[variant]} ${className}`, testID }, children);
}

// src/components/TierTypography.tsx
import React4 from "react";
import { Text as Text2 } from "react-native";
function TierHeadline({
  size = "xl",
  weight = "bold",
  align = "left",
  color,
  as,
  children,
  className = "",
  style,
  testID
}) {
  const sizeClasses = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
    xl: "text-xl",
    "2xl": "text-2xl",
    "3xl": "text-3xl",
    "4xl": "text-4xl"
  };
  const weightClasses = {
    normal: "font-normal",
    semibold: "font-semibold",
    bold: "font-bold"
  };
  const alignClasses = {
    left: "text-left",
    center: "text-center",
    right: "text-right"
  };
  const colorClass = color || "text-dark-gray";
  return /* @__PURE__ */ React4.createElement(
    Text2,
    {
      className: `font-display ${sizeClasses[size]} ${weightClasses[weight]} ${alignClasses[align]} ${colorClass} ${className}`,
      style,
      testID,
      accessibilityRole: as ? "header" : "text",
      ...as ? { "aria-level": parseInt(as.replace("h", "")) } : {}
    },
    children
  );
}
function TierBody({
  size = "base",
  weight = "normal",
  align = "left",
  color,
  children,
  className = "",
  style,
  testID
}) {
  const sizeClasses = {
    xs: "text-xs",
    sm: "text-sm",
    base: "text-base",
    lg: "text-lg"
  };
  const weightClasses = {
    normal: "font-normal",
    medium: "font-medium",
    semibold: "font-semibold"
  };
  const alignClasses = {
    left: "text-left",
    center: "text-center",
    right: "text-right"
  };
  const colorClass = color || "text-dark-gray";
  return /* @__PURE__ */ React4.createElement(
    Text2,
    {
      className: `font-body ${sizeClasses[size]} ${weightClasses[weight]} ${alignClasses[align]} leading-normal ${colorClass} ${className}`,
      style,
      testID,
      accessibilityRole: "text"
    },
    children
  );
}
function NarratorText({
  size = "base",
  align = "left",
  color,
  children,
  className = "",
  testID
}) {
  const sizeClasses = {
    xs: "text-xs",
    sm: "text-sm",
    base: "text-base",
    lg: "text-lg"
  };
  const alignClasses = {
    left: "text-left",
    center: "text-center",
    right: "text-right"
  };
  const colorClass = color || "text-medium-gray";
  return /* @__PURE__ */ React4.createElement(
    Text2,
    {
      className: `font-narrator ${sizeClasses[size]} ${alignClasses[align]} leading-relaxed ${colorClass} ${className}`,
      testID,
      accessibilityRole: "text"
    },
    children
  );
}

// src/components/PatternSpotlightCard.tsx
import React5 from "react";
import { View as View3, Pressable } from "react-native";
import { useTranslation } from "@dramadetector/shared";
function PatternSpotlightCard({
  patternType,
  description,
  frequency,
  context,
  tierLevel,
  onShare,
  className = "",
  testID = "pattern-spotlight-card"
}) {
  const { t } = useTranslation(["features", "common"]);
  if (!tierLevel || tierLevel < 2) {
    return null;
  }
  return /* @__PURE__ */ React5.createElement(
    TierCard,
    {
      variant: "premium",
      className: `tier-reveal-animation ${className}`,
      testID
    },
    /* @__PURE__ */ React5.createElement(View3, { className: "grid grid-cols-12 gap-4" }, /* @__PURE__ */ React5.createElement(View3, { className: "col-span-8" }, /* @__PURE__ */ React5.createElement(
      TierHeadline,
      {
        size: "lg",
        weight: "bold",
        color: "text-gray-900",
        testID: `${testID}-pattern-name`
      },
      patternType
    )), onShare && /* @__PURE__ */ React5.createElement(View3, { className: "col-span-4 flex items-end justify-start" }, /* @__PURE__ */ React5.createElement(
      Pressable,
      {
        onPress: onShare,
        className: "px-4 py-2 border border-accent-gold rounded-tier bg-white active:bg-accent-blush transition-colors",
        testID: `${testID}-share-button`,
        accessibilityLabel: t("features:patternSpotlightCard.accessibility.sharePattern", { patternType, defaultValue: "Share {{patternType}} pattern" })
      },
      /* @__PURE__ */ React5.createElement(
        TierBody,
        {
          size: "base",
          weight: "medium",
          color: "text-accent-gold"
        },
        t("common:share")
      )
    )), /* @__PURE__ */ React5.createElement(View3, { className: "col-span-12" }, /* @__PURE__ */ React5.createElement(
      TierBody,
      {
        size: "base",
        color: "text-gray-700",
        className: "leading-relaxed",
        testID: `${testID}-description`
      },
      description
    )), context && /* @__PURE__ */ React5.createElement(View3, { className: "col-span-12" }, /* @__PURE__ */ React5.createElement(
      TierBody,
      {
        size: "sm",
        color: "text-gray-500",
        className: "italic",
        testID: `${testID}-context`
      },
      context
    )), /* @__PURE__ */ React5.createElement(View3, { className: "col-span-6" }, /* @__PURE__ */ React5.createElement(View3, { className: "inline-flex items-center px-3 py-1 bg-accent-blush border-l-2 border-accent-gold rounded-tier" }, /* @__PURE__ */ React5.createElement(
      TierBody,
      {
        size: "sm",
        weight: "semibold",
        color: "text-gray-900",
        testID: `${testID}-frequency`
      },
      t("features:patternSpotlightCard.detected", { count: frequency, defaultValue: "Detected {{count}} times" })
    ))))
  );
}

// src/components/TransformationInsightCard.tsx
import React6, { useEffect as useEffect2, useState } from "react";
import { View as View4, Pressable as Pressable2, AccessibilityInfo } from "react-native";
import { useTranslation as useTranslation2 } from "@dramadetector/shared";

// src/components/transformation/BubbleTails.tsx
import { useEffect } from "react";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
  Easing
} from "react-native-reanimated";
import Svg, { Path } from "react-native-svg";
function BubbleTail({
  direction,
  delay,
  isReducedMotion
}) {
  const opacity = useSharedValue(0);
  useEffect(() => {
    if (!isReducedMotion) {
      opacity.value = withDelay(
        delay,
        withTiming(1, { duration: 500, easing: Easing.out(Easing.ease) })
      );
    } else {
      opacity.value = 1;
    }
  }, [delay, isReducedMotion]);
  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value
  }));
  const tailConfig = {
    left: {
      style: { position: "absolute", left: -12, top: 30 },
      path: "M 12 0 L 0 10 L 12 20 Z"
    },
    right: {
      style: { position: "absolute", right: -12, top: 50 },
      path: "M 0 0 L 12 10 L 0 20 Z"
    },
    bottom: {
      style: { position: "absolute", bottom: -15 },
      path: "M 0 0 L 10 15 L 20 0 Z"
    }
  };
  const config = tailConfig[direction];
  return /* @__PURE__ */ React.createElement(
    Animated.View,
    {
      style: [
        config.style,
        animatedStyle,
        // Bottom tail needs left positioning as percentage (not supported in static style object)
        direction === "bottom" && { left: "40%" }
      ],
      accessibilityElementsHidden: true
    },
    /* @__PURE__ */ React.createElement(Svg, { width: "20", height: "20", viewBox: "0 0 20 20" }, /* @__PURE__ */ React.createElement(Path, { d: config.path, fill: "#C8A882" }))
  );
}
function MultipleBubbleTails({ isReducedMotion }) {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(BubbleTail, { direction: "left", delay: 0, isReducedMotion }), /* @__PURE__ */ React.createElement(BubbleTail, { direction: "right", delay: 500, isReducedMotion }), /* @__PURE__ */ React.createElement(BubbleTail, { direction: "bottom", delay: 1e3, isReducedMotion }));
}

// src/lib/transformation/transformationUtils.ts
function truncateAtSentence(text, maxLength = 200) {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  const truncated = text.substring(0, maxLength);
  const lastPeriod = truncated.lastIndexOf(".");
  const lastQuestion = truncated.lastIndexOf("?");
  const lastExclamation = truncated.lastIndexOf("!");
  const boundary = Math.max(lastPeriod, lastQuestion, lastExclamation);
  return boundary > 0 ? truncated.substring(0, boundary + 1) : truncated + "...";
}
function getImpactLabel(score) {
  if (score <= 3) return "low";
  if (score <= 7) return "moderate";
  return "high";
}
function getImpactBadgeStyles(impact) {
  const styles = {
    low: "bg-red-100 border-red-500",
    moderate: "bg-yellow-100 border-yellow-500",
    high: "bg-green-100 border-green-500"
  };
  return styles[impact];
}
function isGroupDynamicsTransformation(transformationType) {
  if (!transformationType) return false;
  const groupTypes = [
    "group-alignment",
    "team-cohesion",
    "collaborative-tone",
    "inclusive-language",
    "collective-voice"
  ];
  return groupTypes.includes(transformationType.toLowerCase());
}

// src/components/TransformationInsightCard.tsx
function TransformationInsightCard({
  transformationType,
  beforeText,
  afterText,
  insights,
  impactScore,
  tierLevel,
  onShare,
  className = "",
  testID = "transformation-insight-card"
}) {
  const { t } = useTranslation2(["features", "common"]);
  const [isReducedMotion, setReducedMotion] = useState(false);
  const showBubbleTails = isGroupDynamicsTransformation(transformationType);
  useEffect2(() => {
    const checkReduceMotion = async () => {
      setReducedMotion(await AccessibilityInfo.isReduceMotionEnabled());
    };
    checkReduceMotion();
    const subscription = AccessibilityInfo.addEventListener(
      "reduceMotionChanged",
      setReducedMotion
    );
    return () => {
      subscription.remove();
    };
  }, []);
  if (!tierLevel || tierLevel < 3) {
    return null;
  }
  if (!insights || insights.length === 0) {
    console.warn("[TransformationInsightCard] No insights provided. Minimum 1 required.");
    return null;
  }
  const truncatedBefore = truncateAtSentence(beforeText);
  const truncatedAfter = truncateAtSentence(afterText);
  const impactLabel = getImpactLabel(impactScore);
  const impactBadgeStyles = getImpactBadgeStyles(impactLabel);
  const translatedType = t(`transformationInsightCard.types.${transformationType}`, {
    defaultValue: transformationType
  });
  return /* @__PURE__ */ React6.createElement(View4, { style: { position: "relative" } }, showBubbleTails && /* @__PURE__ */ React6.createElement(MultipleBubbleTails, { isReducedMotion }), /* @__PURE__ */ React6.createElement(
    TierCard,
    {
      variant: "premium",
      className: `bg-black shadow-neumorphic-inset-gold dramatic-reveal-animation ${className}`,
      testID
    },
    /* @__PURE__ */ React6.createElement(View4, { className: "grid grid-cols-12 gap-4" }, /* @__PURE__ */ React6.createElement(View4, { className: "col-span-8" }, /* @__PURE__ */ React6.createElement(
      TierHeadline,
      {
        size: "lg",
        weight: "bold",
        color: "text-white",
        testID: `${testID}-transformation-type`
      },
      translatedType
    )), onShare && /* @__PURE__ */ React6.createElement(View4, { className: "col-span-4 flex items-end justify-start" }, /* @__PURE__ */ React6.createElement(
      Pressable2,
      {
        onPress: onShare,
        className: "px-4 py-2 border border-accent-gold rounded-tier bg-transparent active:bg-accent-gold/20 shadow-neumorphic-outset-gold transition-colors",
        testID: `${testID}-share-button`,
        accessibilityLabel: t("transformationInsightCard.accessibility.shareTransformation", {
          transformationType: translatedType
        })
      },
      /* @__PURE__ */ React6.createElement(TierBody, { size: "base", weight: "medium", color: "text-accent-gold" }, t("common:share"))
    )), /* @__PURE__ */ React6.createElement(View4, { className: "col-span-12 md:col-span-6" }, /* @__PURE__ */ React6.createElement(TierBody, { size: "sm", weight: "semibold", color: "text-accent-gold", className: "mb-2" }, t("transformationInsightCard.labels.before")), /* @__PURE__ */ React6.createElement(
      View4,
      {
        className: "p-4 bg-gray-900 border-l-2 border-red-500 rounded-tier",
        accessibilityLabel: t("transformationInsightCard.accessibility.beforeText")
      },
      /* @__PURE__ */ React6.createElement(
        TierBody,
        {
          size: "base",
          color: "text-gray-300",
          className: "leading-relaxed",
          testID: `${testID}-before-text`
        },
        truncatedBefore
      )
    )), /* @__PURE__ */ React6.createElement(View4, { className: "col-span-12 md:col-span-6" }, /* @__PURE__ */ React6.createElement(TierBody, { size: "sm", weight: "semibold", color: "text-accent-gold", className: "mb-2" }, t("transformationInsightCard.labels.after")), /* @__PURE__ */ React6.createElement(
      View4,
      {
        className: "p-4 bg-gray-900 border-l-2 border-green-500 rounded-tier",
        accessibilityLabel: t("transformationInsightCard.accessibility.afterText")
      },
      /* @__PURE__ */ React6.createElement(
        TierBody,
        {
          size: "base",
          color: "text-gray-100",
          className: "leading-relaxed",
          testID: `${testID}-after-text`
        },
        truncatedAfter
      )
    )), /* @__PURE__ */ React6.createElement(View4, { className: "col-span-12" }, /* @__PURE__ */ React6.createElement(TierBody, { size: "sm", weight: "semibold", color: "text-accent-gold", className: "mb-2" }, t("transformationInsightCard.insights_label")), /* @__PURE__ */ React6.createElement(View4, { className: "space-y-2" }, insights.slice(0, 3).map((insight, index) => /* @__PURE__ */ React6.createElement(View4, { key: index, className: "flex flex-row" }, /* @__PURE__ */ React6.createElement(TierBody, { size: "base", color: "text-accent-gold", className: "mr-2" }, "\u2022"), /* @__PURE__ */ React6.createElement(
      TierBody,
      {
        size: "base",
        color: "text-gray-200",
        className: "flex-1",
        testID: `${testID}-insight-${index}`
      },
      insight
    ))))), /* @__PURE__ */ React6.createElement(View4, { className: "col-span-6" }, /* @__PURE__ */ React6.createElement(
      View4,
      {
        className: `inline-flex items-center px-3 py-1 border-l-2 rounded-tier ${impactBadgeStyles}`
      },
      /* @__PURE__ */ React6.createElement(
        TierBody,
        {
          size: "sm",
          weight: "semibold",
          color: "text-gray-900",
          testID: `${testID}-impact-badge`
        },
        t(`transformationInsightCard.impact.${impactLabel}`)
      )
    )))
  ));
}

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { DRAMA_THRESHOLDS } from "@dramadetector/shared";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
function getDramaCategoryColor(category) {
  const colors = {
    safe: "bg-drama-safe text-white",
    caution: "bg-drama-caution text-white",
    high_drama: "bg-drama-high text-white",
    extreme_drama: "bg-drama-extreme text-white"
  };
  return colors[category];
}
function getDramaScoreColor(score) {
  if (score <= DRAMA_THRESHOLDS.SAFE_MAX) return "text-drama-safe";
  if (score <= DRAMA_THRESHOLDS.CAUTION_MAX) return "text-drama-caution";
  if (score <= DRAMA_THRESHOLDS.HIGH_DRAMA_MAX) return "text-drama-high";
  return "text-drama-extreme";
}
function formatDramaScore(score) {
  if (score === null) {
    return "Support Available";
  }
  return score.toFixed(1);
}

// src/components/drama-badge.tsx
import { getCategoryLabel, getCategoryEmoji } from "@dramadetector/shared";
var dramaBadgeStyles = {
  container: "inline-flex flex-row items-center justify-center rounded-full px-3 py-1.5 gap-1",
  emoji: "text-sm",
  label: "text-sm font-semibold"
};
function getDramaBadgeContent(category, showEmoji = true) {
  return {
    label: getCategoryLabel(category),
    emoji: showEmoji ? getCategoryEmoji(category) : null,
    className: getDramaCategoryColor(category)
  };
}

export {
  TierButton,
  TierCard,
  TierHeadline,
  TierBody,
  NarratorText,
  PatternSpotlightCard,
  TransformationInsightCard,
  cn,
  getDramaCategoryColor,
  getDramaScoreColor,
  formatDramaScore,
  dramaBadgeStyles,
  getDramaBadgeContent
};
//# sourceMappingURL=chunk-RVO37TTB.mjs.map